/**
 * Shared Convenience Fee Calculation Service
 * Single source of truth for convenience fee calculations across backend and frontend
 */

export class ConvenienceFeeService {
  static readonly MIN_DUES_TRACKER_FEE = 1.0;
  static readonly MAX_DUES_TRACKER_FEE = 5.0;

  /**
   * Calculate total convenience fee (dueStracker + Stripe processing fee)
   * @param amount - The base amount
   * @returns Total convenience fee
   */
  static calculateConvenienceFee = (amount: number): number => {
    if (amount === 0) return 0;

    const duesTrackerFee = this.calculateDuesTrackerFee(amount);
    
    // Formula: (amount + duesTrackerFee + stripe_fixed_fee) / (1 - stripe_percentage)
    // Stripe: 2.9% + $0.30
    const totalWithFees = (amount + duesTrackerFee + 0.3) / (1 - 0.029);
    return totalWithFees - amount;
  };

  /**
   * Calculate just the duesTracker fee portion
   * @param amount - The base amount
   * @returns DuesTracker fee only
   */
  static calculateDuesTrackerFee = (amount: number): number => {
    if (amount === 0) return 0;

    return Math.min(
      this.MAX_DUES_TRACKER_FEE,
      Math.max(this.MIN_DUES_TRACKER_FEE, amount * 0.01)
    );
  };

  /**
   * Calculate just the platform (Stripe) fee portion
   * @param amount - The base amount
   * @returns Platform fee only
   */
  static calculatePlatformFee = (amount: number): number => {
    return this.calculateConvenienceFee(amount) - this.calculateDuesTrackerFee(amount);
  };

  /**
   * Calculate ACH-specific convenience fee
   * @param amount - The base amount
   * @returns ACH convenience fee
   */
  static calculateAchConvenienceFee = (amount: number): number => {
    if (amount === 0) return 0;

    return this.calculateDuesTrackerFee(amount);
  };
}
